// F5 web push: the browser side of subscribing.
import { api } from "./api";
import { reportError } from "./report";

export type PushSupport = "supported" | "unsupported" | "needs-install" | "denied";

export function pushSupport(): PushSupport {
  if (typeof window === "undefined" || !("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    // iOS only allows push for installed (home-screen) apps.
    const ios = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
    return ios ? "needs-install" : "unsupported";
  }
  if (Notification.permission === "denied") return "denied";
  return "supported";
}

export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== "supported") return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function enablePush(publicKey: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    reportError("push", new Error(`permission ${permission}`), {}, "notice");
    throw new Error("Notifications are blocked. Allow them in your browser's site settings, then try again.");
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
    await api("/api/push/subscriptions", { method: "POST", json: sub.toJSON() });
  } catch (err) {
    // A browser that refuses to subscribe (wrong VAPID key, push service down) only shows up here.
    reportError("push", err);
    throw err;
  }
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await api("/api/push/subscriptions", { method: "DELETE", json: { endpoint: sub.endpoint } }).catch(() => {});
  await sub.unsubscribe();
}
